export class SurfaceSampler {
  constructor(positionArray, indexArray, offset = { x: 0, y: 0 }) {
    this.positions = positionArray;
    this.indices = indexArray;
    this.offset = offset;
    this.bounds = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
    for (let i = 0; i < positionArray.length; i += 3) {
      const x = positionArray[i] + offset.x, y = positionArray[i + 1] + offset.y;
      this.bounds.minX = Math.min(this.bounds.minX, x); this.bounds.maxX = Math.max(this.bounds.maxX, x);
      this.bounds.minY = Math.min(this.bounds.minY, y); this.bounds.maxY = Math.max(this.bounds.maxY, y);
    }
    const triangleCount = indexArray.length / 3;
    this.cols = Math.max(20, Math.min(120, Math.ceil(Math.sqrt(triangleCount / 14))));
    this.rows = this.cols;
    this.cellW = Math.max(0.001, (this.bounds.maxX - this.bounds.minX) / this.cols);
    this.cellH = Math.max(0.001, (this.bounds.maxY - this.bounds.minY) / this.rows);
    this.buckets = Array.from({ length: this.cols * this.rows }, () => []);
    for (let tri = 0; tri < indexArray.length; tri += 3) this.#insert(tri);
  }
  #vertex(index) {
    const at = index * 3;
    return { x: this.positions[at] + this.offset.x, y: this.positions[at + 1] + this.offset.y, z: this.positions[at + 2] };
  }
  #cellX(x) { return Math.max(0, Math.min(this.cols - 1, Math.floor((x - this.bounds.minX) / this.cellW))); }
  #cellY(y) { return Math.max(0, Math.min(this.rows - 1, Math.floor((y - this.bounds.minY) / this.cellH))); }
  #insert(tri) {
    const a = this.#vertex(this.indices[tri]), b = this.#vertex(this.indices[tri + 1]), c = this.#vertex(this.indices[tri + 2]);
    const x0 = this.#cellX(Math.min(a.x, b.x, c.x)), x1 = this.#cellX(Math.max(a.x, b.x, c.x));
    const y0 = this.#cellY(Math.min(a.y, b.y, c.y)), y1 = this.#cellY(Math.max(a.y, b.y, c.y));
    for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) this.buckets[y * this.cols + x].push(tri);
  }
  translate(dx, dy) {
    const shiftX = Number(dx), shiftY = Number(dy);
    if (!Number.isFinite(shiftX) || !Number.isFinite(shiftY)) return;
    this.offset.x += shiftX;
    this.offset.y += shiftY;
    this.bounds.minX += shiftX;
    this.bounds.maxX += shiftX;
    this.bounds.minY += shiftY;
    this.bounds.maxY += shiftY;
  }
  sample(x, y) {
    if (x < this.bounds.minX || x > this.bounds.maxX || y < this.bounds.minY || y > this.bounds.maxY) return null;
    const bucket = this.buckets[this.#cellY(y) * this.cols + this.#cellX(x)];
    for (const tri of bucket) {
      const a = this.#vertex(this.indices[tri]), b = this.#vertex(this.indices[tri + 1]), c = this.#vertex(this.indices[tri + 2]);
      const den = (b.y - c.y) * (a.x - c.x) + (c.x - b.x) * (a.y - c.y);
      if (Math.abs(den) < 1e-10) continue;
      const u = ((b.y - c.y) * (x - c.x) + (c.x - b.x) * (y - c.y)) / den;
      const v = ((c.y - a.y) * (x - c.x) + (a.x - c.x) * (y - c.y)) / den;
      const w = 1 - u - v;
      if (u >= -1e-6 && v >= -1e-6 && w >= -1e-6) return u * a.z + v * b.z + w * c.z;
    }
    return null;
  }
}
